// App shell. Phone: slim top bar, scrolling main, bottom tab bar (4 tabs + "Lisää").
// PC (≥ 900px): left rail with every section, in groups (nav.ts). Safe-area insets on all edges.
// The theme is chosen in Asetukset only (no toggle in the bars).
// App feel (see also styles/app.css):
// - each tab / section remembers where you were (its last location, and the scroll position per
//   location); tapping the open tab again goes to its root, and at the root scrolls to the top;
// - the Home Screen badge follows today's card count (appBadge.ts);
// - a quiet install hint on the first launch in a browser tab (InstallBanner, never standalone);
// - after start-up, the form index is warmed into the SW cache (lib/offline.ts);
// - accessibility: a "Siirry sisältöön" skip link, focus on the page's h1 after each navigation,
//   document.title per view, a visually hidden h1 on the study screens (Tänään, Vapaa harjoitus);
// - the service worker registers (UpdatePrompt) only once the first screen is ready and painted, so
//   its precache download never competes with the start-up on a slow connection.
// Layout v2 (docs/design/SIVUPALSTA.md; margin/): the working margin's state (MarginProvider: the
// open word, the active line, quick settings — in the URL, so Back closes them), the back bar on
// every non-tab screen, Pika-asetukset (phone top bar; PC margin head, or the rail foot where the
// page has no margin), the one player bar, and the sheet / side panel that hosts the margin where
// no spread does.
import { Suspense, useEffect, useEffectEvent, useLayoutEffect, useRef, useState, type MouseEvent, type RefObject } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigationType } from 'react-router';
import { isPathPart } from '../weekhub/paths';
import { Icon } from '../components/Icon';
import { useT } from '../i18n';
import { useIsAdmin } from '../lib/people';
import { syncNow } from '../state/actions';
import { useApp } from '../state/store';
import { useAppBadge } from './appBadge';
import { InstallBanner } from './InstallBanner';
import { useInstall } from './install';
import { groupNav, INSTALL_PATH, MORE_PATH, visibleNav, type NavItem } from './nav';
import { afterReadyAndPaint } from './startup';
import { useDocumentPrefs } from './theme';
import { UpdatePrompt } from './UpdatePrompt';
import { RecordingUndoToast } from '../recordings/UndoToast';
import { BackBar, BackProvider } from '../margin/BackBar';
import { MarginProvider, withoutMarginParams } from '../margin/context';
import { MarginSheetHost, QuickButton, useMarginHost } from '../margin/hosts';
import { PlayerBar } from '../margin/PlayerBar';
import { useBottomChrome } from './bottomChrome';
// "Mitä uutta" once per person on Tänään, and from Asetukset (shell/tour/)
import { WhatsNewHost } from './tour/WhatsNewHost';
// minutes per activity (docs/PEDAGOGY.md §5.3): one clock for the whole app
import { useActivityClock } from '../pedagogy/clock';

// ---------- where you were ----------

/** Scroll position of .main per location (pathname + search), for this app session. */
const scrollByPath = new Map<string, number>();
/** Last location inside each phone tab ('more' = the Lisää group) and each rail section. */
const lastByTab = new Map<string, string>();
const lastBySection = new Map<string, string>();

const sectionOf = (pathname: string, nav: readonly NavItem[]): NavItem | undefined =>
  nav.find((n) => (n.path === '/' ? pathname === '/' : pathname === n.path || pathname.startsWith(n.path + '/')));

const USER_INPUT = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * Restore the remembered scroll position of `key` in `ref`, retrying while lazy content grows.
 * A location with a hash (a link to a place in a page, e.g. /grammar#cases) is not restored:
 * the page scrolls to that place itself. Neither is `fresh` (a page of a lesson's path reached by a
 * link, Axel 2026-10-10: it opens at its top; Back and a tab you return to still restore).
 */
function useScrollMemory(ref: RefObject<HTMLElement | null>, key: string, anchored = false, fresh: () => boolean = () => false): void {
  const keyRef = useRef(key);
  // read when the location changes, not a reason to restore again by itself
  const isAnchored = useEffectEvent(() => anchored);
  const isFresh = useEffectEvent(() => fresh());
  const restoring = useRef<{ target: number; until: number } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      if (!restoring.current) scrollByPath.set(keyRef.current, el.scrollTop);
    };
    // Any user input ends a pending restore (never fight the finger).
    const cancel = () => {
      restoring.current = null;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    for (const ev of USER_INPUT) el.addEventListener(ev, cancel, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      for (const ev of USER_INPUT) el.removeEventListener(ev, cancel);
    };
  }, [ref]);

  useLayoutEffect(() => {
    keyRef.current = key;
    const el = ref.current;
    if (!el) return;
    if (isAnchored()) {
      restoring.current = null;
      return;
    }
    const target = isFresh() ? 0 : (scrollByPath.get(key) ?? 0);
    el.scrollTop = target;
    if (target <= 0 || Math.abs(el.scrollTop - target) < 2) {
      restoring.current = null;
      return;
    }
    // The section's chunk or its data may still be loading: keep trying for a moment.
    restoring.current = { target, until: performance.now() + 1500 };
    let raf = 0;
    const tick = () => {
      const r = restoring.current;
      if (!r) return;
      el.scrollTop = r.target;
      if (Math.abs(el.scrollTop - r.target) < 2 || performance.now() > r.until) {
        restoring.current = null;
        scrollByPath.set(key, el.scrollTop);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      restoring.current = null;
    };
  }, [ref, key]);
}

// ---------- focus and title after navigation ----------

/** The page's main heading: the first rendered h1 in main (the hidden study h1 counts). */
function mainHeading(main: HTMLElement): HTMLElement | null {
  for (const h of main.querySelectorAll<HTMLElement>('h1')) if (h.getClientRects().length) return h;
  return null;
}

/**
 * After a route change, move focus to the new page's h1 (screen readers announce it, Tab continues
 * from the top of the content). Waits for a lazy section to render. Never takes focus from
 * something inside main that the page focused itself (a study card's button, a search field).
 */
function focusMainHeading(main: HTMLElement): () => void {
  let raf = 0;
  const until = performance.now() + 3000;
  const tick = () => {
    const a = document.activeElement;
    if (a && a !== document.body && main.contains(a)) return;
    const h = mainHeading(main);
    if (h) {
      if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1');
      // No focus cue for a move the person did not make with the keyboard (where supported).
      h.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
      return;
    }
    if (performance.now() < until) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}

/** Move focus to the main content (the skip link). */
function skipToMain(e: MouseEvent) {
  e.preventDefault();
  const main = document.getElementById('main');
  if (!main) return;
  const target = mainHeading(main) ?? main;
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus();
}

// ---------- top bar / rail pieces ----------

/**
 * Sync state in the top bar (`short`: one line that never wraps, the full sentence for screen
 * readers and as the tooltip) and in the rail foot. The live region is always in the DOM; only its
 * content changes, so a screen reader announces "Ei yhteyttä" when it appears.
 */
function SyncChip({ short }: { short?: boolean }) {
  return (
    <span className="sync-live" role="status" aria-live="polite">
      <SyncChipBody short={!!short} />
    </span>
  );
}

function SyncChipBody({ short }: { short: boolean }) {
  const t = useT();
  const sync = useApp((s) => s.sync);
  if (sync.state === 'auth') {
    return (
      // A path the service worker never answers from cache (navigateFallbackDenylist: /api/), so
      // the request reaches Cloudflare Access; functions/api/[[path]].ts redirects back to /.
      <a className="sync-chip is-auth" href="/api/reauth">
        {t.sync.auth}
      </a>
    );
  }
  if (sync.state === 'offline') {
    const full = `${t.sync.offline}${sync.pending ? ` · ${t.sync.pending(sync.pending)}` : ''}`;
    return (
      <span className="sync-chip" title={short ? full : undefined}>
        <Icon name="offline" size={16} />
        {short ? (
          <>
            <span aria-hidden="true">{t.sync.offlineShort(sync.pending)}</span>
            <span className="vh">{full}</span>
          </>
        ) : (
          <span>{full}</span>
        )}
      </span>
    );
  }
  if (sync.state === 'error') {
    return (
      <button type="button" className="sync-chip" onClick={syncNow}>
        {t.sync.error} · {t.sync.retry}
      </button>
    );
  }
  if (sync.pending > 0) {
    const full = t.sync.pending(sync.pending);
    return short ? (
      <span className="sync-chip" title={full}>
        <span aria-hidden="true">{t.sync.pendingShort(sync.pending)}</span>
        <span className="vh">{full}</span>
      </span>
    ) : (
      <span className="sync-chip">{full}</span>
    );
  }
  return null;
}

/** One rail entry: a link to the section (its last place when it is not the current one). */
function RailItem({ n, current, onSame }: { n: NavItem; current: NavItem | undefined; onSame: (root: string, active: boolean) => (e: MouseEvent) => void }) {
  const t = useT();
  return (
    <NavLink
      to={current?.id === n.id ? n.path : (lastBySection.get(n.id) ?? n.path)}
      end={n.path === '/'}
      className="rail-link"
      onClick={onSame(n.path, current?.id === n.id)}
      draggable={false}
    >
      <Icon name={n.icon} />
      <span>{t.nav[n.id]}</span>
    </NavLink>
  );
}

/** The form index into the SW cache (lib/offline.ts, loaded on demand). */
const warmFormIndexLater = () => void import('../lib/offline').then((m) => m.warmFormIndex()).catch(() => undefined);

export function Layout() {
  return (
    <MarginProvider>
      <BackProvider>
        <Shell />
      </BackProvider>
    </MarginProvider>
  );
}

/** The height of `.main` as --main-h: lane B (sticky in the spread) is exactly one screen of it. */
function useMainHeight(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const set = () => el.style.setProperty('--main-h', `${el.clientHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
}

/**
 * The app badge's subscriptions (cards, shared state, lessons, every sync) in a component of their
 * own: in the shell itself each sync re-rendered the whole shell and its rail (docs/audit/performance.md "Pages").
 */
function AppBadge(): null {
  useAppBadge();
  return null;
}

function Shell() {
  const t = useT();
  const { pathname, search, hash, state: locState } = useLocation();
  const theme = useApp((s) => s.theme);
  const lang = useApp((s) => s.lang);
  const phase = useApp((s) => s.phase);
  const dictMissing = useApp((s) => s.dictMissing);
  const install = useInstall();
  useDocumentPrefs(theme, lang);
  const mainRef = useRef<HTMLElement>(null);
  // The admin's sections (Tarkistusjono) are not part of the other person's app at all.
  const nav = visibleNav(useIsAdmin());
  const current = sectionOf(pathname, nav);
  const isSession = typeof current?.session === 'function' ? current.session({ pathname, state: locState }) : !!current?.session;
  const tabs = nav.filter((n) => n.phone === 'tab');
  const moreActive = pathname === MORE_PATH || pathname === INSTALL_PATH || current?.phone === 'more';
  // The margin's parameters (?sana=, ?rivi=) are not a place of their own: scroll memory and the
  // tabs' last locations ignore them.
  const here = pathname + withoutMarginParams(search);
  const host = useMarginHost();
  const tabKey = current?.phone === 'tab' ? current.id : moreActive ? 'more' : null;

  // Remember the last location of the tab and section we are in. Idempotent, so done during
  // render: the links below must already point at it.
  if (tabKey) lastByTab.set(tabKey, here);
  if (current) lastBySection.set(current.id, here);

  // A screen you come back to opens where you left it; a new screen starts at the top. A page of a
  // lesson's path opens at its top when a link leads there (not Back, not a tab you return to).
  const navType = useNavigationType();
  const viaTab = useRef(false);
  useScrollMemory(mainRef, here, hash.length > 1, () => navType !== 'POP' && !viaTab.current && isPathPart(pathname));
  // the tab's own navigation has been restored (the layout effect above): later links are links again
  useEffect(() => {
    viaTab.current = false;
  }, [here]);
  useActivityClock();
  useMainHeight(mainRef);
  // toasts stand above the tabs and the player bar
  useBottomChrome(mainRef);

  // document.title per view: "Sanakirja – Valyrio udrir".
  const view = current ? t.nav[current.id] : pathname === MORE_PATH ? t.more.title : pathname === INSTALL_PATH ? t.install.title : t.shell.notFound.title;
  useEffect(() => {
    document.title = t.shell.title(view);
  }, [t, view]);

  // Focus the new page's heading after a navigation (not on the first load).
  const shownPath = useRef(pathname);
  useEffect(() => {
    if (shownPath.current === pathname) return;
    shownPath.current = pathname;
    const main = mainRef.current;
    return main ? focusMainHeading(main) : undefined;
  }, [pathname]);

  // The installed app keeps unsynced grades in its outboxes: ask the browser not to evict
  // its storage under pressure (granted silently for installed apps; no prompt).
  const persistStorage = useEffectEvent(() => {
    if (install.standalone) void navigator.storage?.persist?.().catch(() => false);
  });
  // The service worker (UpdatePrompt registers it) waits for the first screen.
  const [started, setStarted] = useState(false);
  useEffect(() => {
    let live = true;
    void afterReadyAndPaint().then(() => {
      if (live) setStarted(true);
      persistStorage();
    });
    return () => {
      live = false;
    };
  }, []);

  // Warm the form index into the SW cache once the app has settled (production only).
  useEffect(() => {
    if (!import.meta.env.PROD || phase !== 'ready') return;
    const id = window.setTimeout(() => {
      warmFormIndexLater();
    }, 8000);
    return () => window.clearTimeout(id);
  }, [phase]);

  /** The open tab goes to its root; any other tab returns to where you left it. */
  const tabTarget = (key: string, root: string, active: boolean) => (active ? root : (lastByTab.get(key) ?? root));
  /** Tapping the open tab at its root scrolls to the top (no animation: DESIGN.md Motion). On the
   *  iPhone this is the way up: iOS's status-bar tap scrolls only the document, never .main. */
  const onSameTab = (root: string, active: boolean) => (e: MouseEvent) => {
    // another tab or section: back where you left it, scroll position included
    if (!active) viaTab.current = true;
    if (active && pathname === root && !search) {
      e.preventDefault();
      mainRef.current?.scrollTo({ top: 0 });
      scrollByPath.set(here, 0);
    }
  };

  return (
    <div className={isSession ? 'shell is-session' : 'shell'}>
      {/* iOS Home Screen app with a translucent status bar: keeps its white symbols legible on the
          light theme (styles/app.css). Zero height everywhere else. */}
      <div className="status-strip" aria-hidden="true" />
      <a className="skip-link" href="#main" onClick={skipToMain}>
        {t.shell.skip}
      </a>
      {/* PC: the rail is the app's header (banner landmark: the mark, the grouped navigation and
          sync). It was an unlabelled <aside>, which failed axe landmark-unique. The phone top bar
          is the other <header>; only one of the two is displayed at a time. */}
      <header className="rail only-desk">
        <span className="mark hv">{t.appName}</span>
        <nav aria-label={t.nav.label} className="rail-navs">
          {groupNav(nav).map(({ group, items }) =>
            group === 'app' ? (
              // Sovellus sits in the rail foot, without a label of its own.
              <ul key={group} className="rail-nav rail-app" aria-label={t.nav.groups.app}>
                {items.map((n) => (
                  <li key={n.id}>
                    <RailItem n={n} current={current} onSame={onSameTab} />
                  </li>
                ))}
                {install.standalone ? null : (
                  <li className="install-hint">
                    <NavLink to={INSTALL_PATH} className="rail-link" draggable={false}>
                      <Icon name="install" />
                      <span>{t.install.moreRow}</span>
                    </NavLink>
                  </li>
                )}
              </ul>
            ) : (
              <div key={group} className="nav-group">
                <p className="nav-group-h" id={`nav-g-${group}`}>
                  {t.nav.groups[group]}
                </p>
                <ul className="rail-nav" aria-labelledby={`nav-g-${group}`}>
                  {items.map((n) => (
                    <li key={n.id}>
                      <RailItem n={n} current={current} onSame={onSameTab} />
                    </li>
                  ))}
                </ul>
              </div>
            ),
          )}
        </nav>
        <div className="rail-foot">
          {/* Pika-asetukset: in lane B's head on a spread at ≥ 1180px, here everywhere else */}
          {host === 'lane' ? null : <QuickButton variant="rail" />}
          <SyncChip />
        </div>
      </header>

      <div className="shell-col">
        <header className="topbar only-mob">
          {/* the page's place: the back bar on every non-tab screen, else the app's name */}
          <span className="topbar-title">
            <BackBar section={current} nav={nav} where="topbar" />
            <span className="mark hv">{t.appName}</span>
          </span>
          <SyncChip short />
          <QuickButton variant="topbar" />
        </header>
        {/* Never on a study screen (it would push the card down), and not on Lisää or the install
            page, which carry their own install affordance. */}
        {isSession || pathname === MORE_PATH || pathname === INSTALL_PATH ? null : <InstallBanner where="shell" />}
        {/* A new device offline: nothing works without the dictionary, so say why (state/actions.ts). */}
        {dictMissing ? (
          <p className="dict-missing" role="status">
            {t.shell.dictOffline}
          </p>
        ) : null}
        <main className="main" ref={mainRef} id="main">
          {/* Tänään and Vapaa harjoitus have no visible title while a session runs; hidden by CSS
              (:has) whenever the page shows its own h1. */}
          {current?.id === 'today' || current?.id === 'free' ? (
            <h1 className="vh page-h-hidden" tabIndex={-1}>
              {t.nav[current.id]}
            </h1>
          ) : null}
          {/* PC: "← <section>" above a page below a section root (the phone has it in the top bar) */}
          <BackBar section={current} nav={nav} where="page" />
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </main>
        {/* the one player: here on the phone (above the tabs, only while a line is active) and on
            PC pages without a spread; a spread has it at the foot of lane B. Never during a study
            session (.shell.is-session): the card and the thumb zone keep the phone's height. */}
        {host === 'lane' || isSession ? null : <PlayerBar where="shell" />}
        <nav className="tabbar only-mob" aria-label={t.nav.label}>
          {tabs.map((n) => {
            const active = current?.id === n.id;
            return (
              <Link
                key={n.id}
                to={tabTarget(n.id, n.path, active)}
                className={active ? 'tab active' : 'tab'}
                aria-current={active ? 'page' : undefined}
                onClick={onSameTab(n.path, active)}
                draggable={false}
              >
                <Icon name={n.icon} />
                <span>{t.nav[n.id]}</span>
              </Link>
            );
          })}
          <Link
            to={tabTarget('more', MORE_PATH, moreActive)}
            className={moreActive ? 'tab active' : 'tab'}
            aria-current={moreActive ? 'page' : undefined}
            onClick={onSameTab(MORE_PATH, moreActive)}
            draggable={false}
          >
            <Icon name="more" />
            <span>{t.nav.more}</span>
          </Link>
        </nav>
      </div>
      {/* the margin where no spread hosts it: the word / settings sheet, the PC side panel */}
      <MarginSheetHost />
      {started ? <UpdatePrompt /> : null}
      {/* "Äänite poistettu · Kumoa" (recordings/UndoToast.tsx) */}
      <RecordingUndoToast />
      <AppBadge />
      <WhatsNewHost />
    </div>
  );
}
